/**
 * LetterPreview renderer · iter303aj-fix2
 *
 * Runner card that previews a letter whose content is admin-editable via
 * Script Audit Studio (`app_settings.status_letters.{letter_key}`). By
 * default shows the LIVE PDF (same route the Studio uses) inside an
 * iframe so operators see exactly what the client will receive. A
 * "Text" toggle drops back to the token-filled HTML body as a safety
 * net (useful when the PDF endpoint 501s or the iframe fails to load).
 *
 * action.params:
 *   • letter_key   — status_letters slot key (e.g. "appraisal_booked",
 *                    "appraisal_confirmation")
 *   • letter_label — human-readable card title
 *   • pdf_route    — API path template with {appraisal_id} placeholder
 *   • prompt       — operator instruction text
 *
 * Auto-detects DRAFT status from `letter.is_draft` and shows a DRAFT badge.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { FileText, ExternalLink, AlertTriangle, Eye, Type } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Simple {token} → value replacer (matches backend _apply_tokens).
// SEC-001 fix (2026-02c) — HTML-escape token values before substitution
// so operator-supplied strings (client names, addresses) never reach
// `dangerouslySetInnerHTML` as raw HTML. Legitimate admin-authored
// letter-body markup still renders (the template body itself is not
// escaped · only the substituted token values are).
function _escapeHTML(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function fillTokens(text, tokens) {
  if (!text) return '';
  return text.replace(/\{([A-Za-z0-9_]+)\}/g, (m, k) => {
    const v = tokens[k] || tokens[k.toLowerCase()];
    return v == null || v === '' ? m : _escapeHTML(v);
  });
}

// iter303ak-fix23j · Dynamic letter-key selector. Given a selector name
// and capturedData, return the resolved letter key. The decision tree
// lives in the shared helper (Batch B, 2026-02) so LetterPreview.js
// and SendBundle.js share ONE source of truth, and the branching is
// Jest-testable in isolation.
import { selectAcceptedLetterKey } from './_letterSelector';
import DOMPurify from 'dompurify';

function selectLetterKey(selector, capturedData) {
  return selectAcceptedLetterKey(selector, capturedData, null);
}

export default function LetterPreview({ action, capturedData, onNext, onBack, onCancel, canBack }) {
  const params = action.params || {};
  // iter303ak-fix23j · Support dynamic letter_key via a selector param.
  // Static letter_key still wins if both are provided.
  const letterKey = params.letter_key
    || selectLetterKey(params.letter_key_selector, capturedData)
    || '';
  const letterLabel = params.letter_label || 'Letter';
  const pdfRoute = params.pdf_route || (letterKey ? `/reports/status-letter/${letterKey}/{appraisal_id}` : '');
  const appraisalId = capturedData?.appraisal_id;

  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [viewMode, setViewMode] = useState('pdf'); // 'pdf' | 'text'
  const [pdfBust] = useState(() => Date.now());

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await api.get(`/app-settings/status-letters/${letterKey}`);
      setLetter(data);
    } catch (e) {
      // iter303ak-fix20 · Trevor's Task 3 has letters that aren't in
      // the Status/Action Letters library (Quote Letters + internal
      // reports). Instead of failing, fall back to a stub — the PDF
      // preview panel below still renders from `pdf_route`.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || '';
      const isMissingTemplate = status === 404 || status === 501
        || /not yet designed|no template/i.test(detail);
      if (isMissingTemplate) {
        setLetter({ auto_generated: true, subject: '', body_text: '' });
        // Don't show a red error — the PDF panel is the source of truth.
        setErr('');
      } else {
        setErr(detail || e.message);
      }
    } finally { setLoading(false); }
  }, [letterKey]);

  useEffect(() => { load(); }, [load]);

  // Build lightweight token dict from captured_data for the preview.
  const client = capturedData?.client_record || {};
  const prop   = capturedData?.property_record || {};
  const tokens = {
    Client_name: [client.name_title, client.name_first, client.name_last].filter(Boolean).join(' ') || '(client name)',
    Property_Address: [
      [prop.property_address_num, prop.property_address_street].filter(Boolean).join(' '),
      prop.property_address_suburb, prop.property_address_city,
    ].filter(Boolean).join(', ') || '(property address)',
    Client_phone: client.phone_mobile || client.phone_home || '(phone)',
    Client_email: client.email || '(email)',
    Client_Address: [
      [client.address_num, client.address_street].filter(Boolean).join(' '),
      client.address_suburb, client.address_city,
    ].filter(Boolean).join(', ') || '(client address)',
    // iter303aj-specific tokens for the Confirmation Letter.
    preferred_time_display: capturedData?.preferred_time_display
      || capturedData?.preferred_time_capture?.preferred_time_display
      || '(preferred time)',
    // v10.26 (Feb 2026 · UAT A999682) — Canonical Studio labels
    // (source: NewAppraisalKindButton.js APPRAISAL_KINDS). Same map
    // lives in `reports.py::report_variant_map` — keep them in sync.
    report_variant_label: ({
      free_estimate:  'Instant Free',
      single_defect:  'Detailed 1-Comment',
      multi_defect:   'Detailed Multi',
      quote_only:     'Quote',
    })[capturedData?.['appraisal.report_variant']] || '(report type)',
    primary_quote_types: (capturedData?.['appraisal.primary_quote_types'] || []).join(', ') || '(trades)',
    appraiser_name: capturedData?.appraiser_pick?.name
      || capturedData?.['appraiser_pick']?.name
      || '(appraiser)',
    ethnic_greeting: 'Dear',
    ethnic_thankyou: 'Thank you',
  };

  // iter303aj-fix2 — Live PDF URL. Mirrors ScriptAuditPage.js pdfPreviewUrl
  // pattern: append ?token= so the iframe can authenticate via backend's
  // query-param fallback in get_current_user (iframes can't send Bearer
  // headers), and a _bust= param so upstream Studio edits show up on
  // remount.
  const pdfUrl = useMemo(() => {
    if (!pdfRoute || !appraisalId) return '';
    const base = process.env.REACT_APP_BACKEND_URL;
    const tok = typeof window !== 'undefined' ? localStorage.getItem('access_token') : '';
    const q = `_bust=${pdfBust}`;
    return `${base}/api${pdfRoute.replace('{appraisal_id}', appraisalId)}?${q}`;
  }, [pdfRoute, appraisalId, pdfBust]);

  const isDraft = !!letter?.is_draft;

  // iter303ak-fix22 · Track C v2 · Deep-link into Script Audit Studio
  // for editing the letter template. Maps letter_key → studio variant
  // + active_class. Studio restores from localStorage on mount.
  const openInStudio = () => {
    if (!letterKey) return;
    let variant, active_class;
    if (letterKey === 'quotation_letter' || letterKey === 'invoice_letter') {
      variant = letterKey;
      active_class = 'quote_letters';
    } else {
      variant = `status_${letterKey}`;
      active_class = 'status_letters';
    }
    try {
      localStorage.setItem('script-audit-last-nav', JSON.stringify({ variant, active_class }));
    } catch { /* non-blocking */ }
    window.open('/script-audit', '_blank', 'noopener,noreferrer');
  };

  return (
    <div data-testid="wf-letter-preview">
      <div className="bg-gradient-to-br from-sky-50 to-white border-l-4 border-sky-400 rounded-r-lg p-5 mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-sky-600 font-semibold mb-2">
          <FileText size={12} /> {letterLabel}
          {isDraft && (
            <span data-testid="wf-letter-draft-badge"
              className="normal-case text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5 ml-1 inline-flex items-center gap-1">
              <AlertTriangle size={10} /> DRAFT · needs audit
            </span>
          )}
        </div>
        <div className="text-slate-700 text-sm">{params.prompt || 'Review the letter and Continue when ready.'}</div>
      </div>

      {err && <div className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{err}</div>}

      {/* iter303aj-fix2 — View mode toggle: PDF (default) vs Text (safety net). */}
      {pdfUrl && (
        <div className="mb-3 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" data-testid="wf-letter-view-toggle">
          <button
            onClick={() => setViewMode('pdf')}
            data-testid="wf-letter-view-pdf"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              viewMode === 'pdf' ? 'bg-white text-sky-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}>
            <Eye size={12} /> PDF Preview
          </button>
          <button
            onClick={() => setViewMode('text')}
            data-testid="wf-letter-view-text"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
              viewMode === 'text' ? 'bg-white text-sky-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}>
            <Type size={12} /> Text
          </button>
        </div>
      )}

      {/* PDF view (default) — live iframe matching Studio parity. */}
      {viewMode === 'pdf' && pdfUrl && (
        <div className="mb-5 border border-slate-200 rounded-lg bg-slate-50 overflow-hidden" data-testid="wf-letter-pdf-iframe-wrap">
          <iframe
            src={pdfUrl}
            title={`${letterLabel} PDF preview`}
            data-testid="wf-letter-pdf-iframe"
            className="w-full bg-white"
            style={{ height: '560px', border: 'none' }}
          />
        </div>
      )}

      {/* PDF view but no appraisal_id — clear placeholder. */}
      {viewMode === 'pdf' && !pdfUrl && (
        <div className="mb-5 p-5 border border-dashed border-amber-300 bg-amber-50 rounded text-sm text-amber-800" data-testid="wf-letter-pdf-missing-appraisal">
          <strong>PDF preview needs a saved appraisal.</strong> Complete the earlier &ldquo;Schedule Appraisal&rdquo; steps first, or switch to the Text preview.
        </div>
      )}

      {/* Text view (fallback) — token-filled HTML body. */}
      {viewMode === 'text' && (
        <>
          {loading ? (
            <div className="p-6 text-sm text-slate-400 italic">Loading letter content…</div>
          ) : letter ? (
            <div className="mb-5 border border-slate-200 rounded-lg bg-white p-6 max-h-[420px] overflow-y-auto"
              data-testid="wf-letter-body">
              {letter.title && (
                <div className="text-lg font-bold text-slate-800 mb-4 text-center pb-2 border-b border-slate-100">
                  {fillTokens(letter.title, tokens)}
                </div>
              )}
              <div className="space-y-3 text-sm text-slate-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize([
                  letter.client_greeting,
                  letter.purpose_body || letter.intro_body,
                  letter.details_prefix,
                  (letter.client_details_items || letter.booking_items || '').split('\n').map(l => l.trim()).filter(Boolean).map(l => `<div class="text-slate-600">${l}</div>`).join(''),
                  letter.authorisation_body || letter.next_steps_body,
                  letter.quality_statement,
                  letter.closing_body,
                  letter.signoff && `<div class="mt-4">${letter.signoff}</div>`,
                  letter.signer_name && `<div class="font-semibold">${letter.signer_name}</div>`,
                  letter.disclaimer && `<div class="text-xs text-slate-500 mt-4 italic">${letter.disclaimer}</div>`,
                ].filter(Boolean).map(s => `<p>${fillTokens(s, tokens)}</p>`).join('')) }} />
            </div>
          ) : (
            <div className="mb-5 p-4 text-sm text-slate-400 italic">No content yet — needs authoring in Script Audit Studio.</div>
          )}
        </>
      )}

      <div className="flex items-center gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-back-btn"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-cancel-btn"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        {/* iter303ak-fix22 · Edit template in Studio (opens new tab) */}
        {letterKey && (
          <button
            onClick={openInStudio}
            data-testid="wf-letter-edit-in-studio"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-indigo-500 text-indigo-700 font-semibold rounded hover:bg-indigo-50"
            title="Open Script Audit Studio to edit this letter template — changes propagate to future runs">
            <ExternalLink size={14} /> Edit template in Studio
          </button>
        )}
        {pdfUrl && (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="wf-letter-preview-pdf"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-sky-500 text-sky-700 font-semibold rounded hover:bg-sky-50">
            <ExternalLink size={14} /> Open in new tab
          </a>
        )}
        <button
          onClick={onNext}
          data-testid="wf-letter-continue-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700">
          Looks good — Continue →
        </button>
      </div>
    </div>
  );
}
